//@author: devin
import { NgTemplateOutlet } from "@angular/common";
import {
    booleanAttribute,
    Component,
    computed,
    DestroyRef,
    DOCUMENT,
    effect,
    ElementRef,
    inject,
    input,
    linkedSignal,
    output,
    signal,
    TemplateRef,
    untracked,
    viewChild,
    ViewEncapsulation,
} from "@angular/core";
import { NgbPopover, Placement, PlacementArray } from "@ng-bootstrap/ng-bootstrap";
import { fromEvent, merge } from "rxjs";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { AngBridgeUtil } from "../../util/ang.bridge.util";

@TypeManagerDecorator("s25-popover")
@Component({
    selector: "s25-popover",
    host: {
        "[class.ngInlineBlock]": "inline()",
        "(mouseleave)": "catchCloseEvent($event)",
        "(focusout)": "catchCloseEvent($event)",
        "(mouseenter)": "catchOpenEvent($event)",
        "(click)": "catchOpenEvent($event)",
    },
    template: `
        @if (disabled()) {
            <ng-container [ngTemplateOutlet]="content"></ng-container>
        } @else {
            <div
                #po="ngbPopover"
                [popoverClass]="popoverClass()"
                [ngbPopover]="popoverTemplate() ?? modelBean()?.popoverTemplate"
                [popoverContext]="popoverContext()"
                [autoClose]="autoClose()"
                (shown)="currentState.set('open')"
                (hidden)="currentState.set('closed')"
                (mouseenter)="catchOpenEvent($event)"
                (focusin)="catchOpenEvent($event)"
                (keydown.enter)="catchOpenEvent($event)"
                [container]="onBody() ? 'body' : null"
                [placement]="placement()"
                tabindex="0"
            >
                <ng-container [ngTemplateOutlet]="content"></ng-container>
            </div>
        }
        <ng-template #content>
            <ng-content></ng-content>
        </ng-template>
    `,
    styles: `
        div {
            height: 100%;
        }
    `,
    standalone: true,
    encapsulation: ViewEncapsulation.Emulated,
    imports: [NgbPopover, NgTemplateOutlet],
})
export class PopoverComponent<TContext = unknown> {
    public static readonly DEFAULT_CLOSE_DELAY_MS: number = 600;
    public static readonly DEFAULT_OPEN_DELAY_MS: number = 500;

    public static sanitizePlacement(value?: PlacementArray): Placement[] {
        if (!value) return ["auto"];
        const asArray = !Array.isArray(value) ? value.split(" ") : value;
        return asArray.filter((p): p is Placement => validPlacements.has(p));
    }

    /** @deprecated use granular inputs instead */
    readonly modelBean = input<PopoverModelBean<TContext>>();

    readonly popoverTemplate = input.required<string | TemplateRef<TContext>>();
    readonly popoverContext = input<TContext>();
    readonly isTooltip = input<boolean>(false);
    //default to popover on body https://ng-bootstrap.github.io/#/guides/positioning
    readonly onBody = input<boolean>(true);
    readonly autoClose = input<boolean | "inside" | "outside">("outside");
    readonly openTrigger = input<"mouseenter" | "click">("mouseenter");
    readonly closeTrigger = input<"mouseleave" | "click">("mouseleave");
    readonly openDelayMillis = input<number>(PopoverComponent.DEFAULT_OPEN_DELAY_MS);
    readonly closeDelayMillis = input<number>(PopoverComponent.DEFAULT_CLOSE_DELAY_MS);
    /**
     * Set to true to prevent pop-overs
     */
    readonly disabled = input(false, { transform: booleanAttribute });
    readonly inline = input<boolean>(true);
    readonly placement = input(["auto"] satisfies Placement[], { transform: PopoverComponent.sanitizePlacement });
    /**
     * Additional CSS class(es) to apply to the popover
     */
    readonly popoverClassInput = input<string | undefined>(undefined, { alias: "popoverClass" });
    readonly shown = output<void>();
    readonly hidden = output<void>();
    readonly initialized = output<boolean>();

    protected readonly elementRef = inject(ElementRef);
    protected readonly destroyRef = inject(DestroyRef);
    protected readonly document = inject(DOCUMENT);

    protected readonly popoverOutlet = viewChild(NgbPopover);

    readonly uuid = "s25Popover-" + Date.now() + "-" + Math.floor(Math.random() * 10000);
    protected readonly popoverClass = computed(
        () => `${this.popoverClassInput() ?? this.modelBean()?.popoverClass ?? ""} ${this.uuid}`,
    );
    readonly popoverElement = signal<Element | undefined>(undefined);
    protected readonly prevFocusedElement = signal<HTMLElement | undefined>(undefined);
    /**
     * If this is "open", then the popover is definitely open and visible to the user.
     * Otherwise, the popover is definitely closed and not visible.
     */
    protected readonly currentState = signal<"open" | "closed">("closed");
    /**
     * Setting this will eventually update the state with a configurable delay. This is useful
     * for debouncing changes to the state to reduce flicker.
     */
    protected readonly desiredState = linkedSignal({
        source: () => this.currentState,
        computation: (state) => state(),
    });
    // whether we should try to autofocus content when the popover opens
    protected readonly shouldFocusContent = signal<boolean>(false);

    /** when returning focus to the previous element, ignore the open event that may be triggered */
    #ignoreOpenEvent = false;

    constructor() {
        AngBridgeUtil.init(this);
        this.openOrCloseWithDelay();
        this.handleOpenAndClose();
        this.catchPopoverEnterExitEvents();
        this.closeWhenDisabled();
    }

    private openOrCloseWithDelay() {
        effect((onCleanup) => {
            if (this.desiredState() === this.currentState()) {
                // no action necessary
                return;
            } else if (this.desiredState() === "closed") {
                this.shouldFocusContent.set(false);
                const delayedClose = setTimeout(() => this.forceClose(), this.closeDelayMillis());
                onCleanup(() => clearTimeout(delayedClose));
            } else if (this.desiredState() === "open" && !this.disabled()) {
                const delayedOpen = setTimeout(() => this.popoverOutlet().open(), this.openDelayMillis());
                onCleanup(() => clearTimeout(delayedOpen));
            }
        });
    }

    private handleOpenAndClose() {
        effect((onCleanup) => {
            if (this.currentState() === "open") {
                const popoverContent = document.querySelector("." + this.uuid);
                if (untracked(() => this.shouldFocusContent())) {
                    // TODO: allow external components to define this focus target behavior
                    const newFocusedElement = popoverContent.querySelector<HTMLElement>(`input, [tabindex="0"]`);
                    if (newFocusedElement) {
                        this.prevFocusedElement.set(document.activeElement as HTMLElement);
                        newFocusedElement.focus();
                    }
                }
                this.popoverElement.set(popoverContent);
                this.shown.emit();
                this.initialized.emit(true);
                onCleanup(() => {
                    if (this.currentState() === "closed" && !this.destroyRef.destroyed) {
                        this.hidden.emit();
                    }
                });
            } else if (this.currentState() === "closed") {
                this.shouldFocusContent.set(false);
                this.popoverElement.set(undefined);
                const prevFocusedElement = this.prevFocusedElement();
                if (prevFocusedElement) {
                    this.prevFocusedElement.set(undefined);
                    // Don't do anything if focus is already somewhere else
                    if (this.document.activeElement && this.document.activeElement !== this.document.body) {
                        return;
                    }
                    // focus the element, but make sure we ignore the event if it lands inside the host
                    this.#ignoreOpenEvent = true;
                    const resetIgnore = setTimeout(() => (this.#ignoreOpenEvent = false), 0);
                    onCleanup(() => clearTimeout(resetIgnore));
                    prevFocusedElement.focus({ preventScroll: true });
                }
            }
        });
    }

    private catchPopoverEnterExitEvents() {
        effect((onCleanup) => {
            const popoverContent = this.popoverElement();
            if (popoverContent) {
                // if the user exits the host element, desired state will be set to "closed", but they may
                // then enter the popover. in this case, we need to update the desired state back to "open"
                const keepOpenOnInteract = merge(
                    fromEvent(popoverContent, "mouseenter"),
                    fromEvent(popoverContent, "click"),
                    fromEvent(popoverContent, "focusin"),
                ).subscribe(() => this.desiredState.set("open"));
                onCleanup(() => keepOpenOnInteract.unsubscribe());

                const closeOnLeave = fromEvent(popoverContent, "mouseleave").subscribe((event: UIEvent) =>
                    this.catchCloseEvent(event),
                );
                onCleanup(() => closeOnLeave.unsubscribe());
            }
        });
    }

    private closeWhenDisabled() {
        effect(() => {
            if (this.disabled()) {
                this.forceClose();
            }
        });
    }

    protected catchOpenEvent($event: any) {
        if (this.#ignoreOpenEvent) {
            return;
        }
        if (!this.disabled() && this.shouldEventTriggerOpen($event)) {
            this.desiredState.set("open");
            if (this.currentState() !== "open" && this.elementRef.nativeElement.contains($event.target)) {
                this.shouldFocusContent.set(true);
            }
        }
    }

    private shouldEventTriggerOpen($event: any) {
        return (
            $event.type === this.openTrigger() ||
            ($event.type === "focusin" && this.isTooltip()) ||
            (this.openTrigger() === "click" && $event.key === "Enter")
        );
    }

    protected catchCloseEvent($event: any) {
        if ($event.type === this.closeTrigger() || ($event.type === "focusout" && this.isTooltip())) {
            this.desiredState.set("closed");
        }
    }

    forceOpen() {
        this.desiredState.set("open");
        this.shouldFocusContent.set(true);
        this.popoverOutlet()?.open?.();
    }

    forceClose() {
        this.desiredState.set("closed");
        this.popoverOutlet()?.close?.();
    }
}

const validPlacements = new Set<string>([
    "auto",
    "top",
    "top-start",
    "top-left",
    "top-end",
    "top-right",
    "bottom",
    "bottom-start",
    "bottom-left",
    "bottom-end",
    "bottom-right",
    "start",
    "start-top",
    "start-bottom",
    "left",
    "left-top",
    "left-bottom",
    "end",
    "end-top",
    "end-bottom",
    "right",
    "right-top",
    "right-bottom",
] satisfies Placement[]);

// still used in s25.multiselect, s25.md.tag.picker, s25.generic.dropdown and a couple others
export type PopoverModelBean<TContext = unknown> = {
    readonly popoverTemplate?: string | TemplateRef<TContext>;
    readonly popoverClass?: string;
};
